import {
  CardRating,
  GameDetails,
  GameMode,
  GameStatus,
  GreenCardRating,
  PlayerDetails,
  RemoteGame,
} from "../types/Game";
import Firebase, { FirebaseCollection } from "./Firebase";
import GameApi from "./GameApi";
import UserSession from "./UserSession";
import Localforage from "./Localforage";
import DateUtil from "././../util/Date";
import {
  ENDING_BOARD_POSITION,
  FINAL_BOARD_POSITION,
  SINGLE_DIE_POSITION,
} from "../config/Constants";

const GAME_KEY = "CURRENT_GAME";
const MAGNIFYING_GLASS_DEFAULT = 2;
const POINTS_DEFAULT = 0;

const createGame = async (mode: GameMode) => {
  const user = await UserSession.getUser();
  if (!user) {
    throw new Error("User information is not availble");
  }
  const { data: game } = await GameApi.createGame(user.userId);

  const gameDetails: GameDetails = {
    mode,
    ownerUserId: user.userId,
    ownerUserName: user.name,
    gameId: game.gameId,
    status:
      mode === GameMode.PlayWithFriends
        ? GameStatus.WaitingPlayers
        : GameStatus.RollDice,
    ttl: game.expiresAt,
    diceResult: [],
    turnUserId: user.userId,
    turnExpAt: game.expiresAt,
  };

  const playerDetails: PlayerDetails = {
    ttl: game.expiresAt,
    socialNetwork: user.socialNetwork,
    timeStamp: DateUtil.getTimeStamp(),
    userId: user.userId,
    gameId: game.gameId,
    name: user.name,
    profileUrl: user.profileUrl,
    boardPosition: 0,
    magnifyGlass: [MAGNIFYING_GLASS_DEFAULT],
    points: [POINTS_DEFAULT],
    finishTimeStamp: 0,
    countryCode: user.countryCode,
  };
  await Firebase.authenticateUser(user.token);
  await Firebase.addDocument(FirebaseCollection.Games, gameDetails);
  await Firebase.addDocument(FirebaseCollection.Players, playerDetails);
  await saveGame(game);
  return game;
};

const joinToGame = async () => {
  const user = await UserSession.getUser();

  // to check if the user is already in the same game

  if (!user) {
    throw new Error("User information is not availble");
  }

  const game = await getCurrentGame();

  if (!game) {
    throw new Error("There is not active game to join");
  }

  await Firebase.authenticateUser(user.token);

  const isPlayerInGame = await Firebase.isPlayerInGame(
    user.userId,
    game.gameId
  );

  if (isPlayerInGame) {
    return game;
  }

  const playerDetails: PlayerDetails = {
    ttl: game.expiresAt,
    socialNetwork: user.socialNetwork,
    timeStamp: DateUtil.getTimeStamp(),
    userId: user.userId,
    gameId: game.gameId,
    name: user.name,
    profileUrl: user.profileUrl,
    magnifyGlass: [MAGNIFYING_GLASS_DEFAULT],
    points: [POINTS_DEFAULT],
    boardPosition: 0,
    finishTimeStamp: 0,
    countryCode: user.countryCode,
  };

  await Firebase.addDocument(FirebaseCollection.Players, playerDetails);
  return game;
};

const getCurrentGame = (): Promise<RemoteGame | null> => {
  return Localforage.getItemWithExpiry<RemoteGame>(GAME_KEY);
};

const removeCurrentGame = () => {
  return Localforage.removeItem(GAME_KEY);
};

const getCurrentGameId = async (): Promise<string | undefined> => {
  const { gameId } =
    (await Localforage.getItemWithExpiry<RemoteGame>(GAME_KEY)) || {};
  return gameId;
};

const getPlayerInGame = async (gameId: string): Promise<PlayerDetails[]> => {
  return Firebase.getPlayersByGame(gameId);
};

const saveGame = (game: RemoteGame) => {
  return Localforage.saveItemWithExpiry(GAME_KEY, game, 86400000);
};

const saveGameInvite = async (gameId: string): Promise<void> => {
  const { data } = await GameApi.getGame(gameId);
  if (data) {
    saveGame(data);
  }
};

const getCurrentGameIdInvite = async (): Promise<string | undefined> => {
  return getCurrentGameId();
};

const rateGreenCard = (data: CardRating) => {
  const greenCardRating: GreenCardRating = {
    ...data,
    ttl: DateUtil.getTimeStampWithExtraHours(24),
  };
  return Firebase.addDocument(
    FirebaseCollection.GreenCardRating,
    greenCardRating
  );
};

const getTotal = (numbers: number[]) => {
  return Math.round(
    numbers
      ? numbers.reduce(
          (accumulator, currentValue) => accumulator + currentValue,
          0
        )
      : 0
  );
};

const getDiceEndingPosition = (
  dice: number[],
  currentPosition: number,
  gameMode: GameMode
): number | undefined => {
  const newPosition = currentPosition + getTotal(dice);

  if (gameMode === GameMode.PlayAlone) {
    return newPosition >= FINAL_BOARD_POSITION
      ? FINAL_BOARD_POSITION
      : undefined;
  }

  if (newPosition >= ENDING_BOARD_POSITION && newPosition <= SINGLE_DIE_POSITION) {
    return Math.max(...dice) + newPosition;
  }

  return undefined;
};


const getGreenCardScore = (cardId: string, gameId: string, userId: string) =>
  Firebase.getGreenCardRating(userId, gameId, cardId);

export default {
  createGame,
  getCurrentGameId,
  saveGameInvite,
  getCurrentGameIdInvite,
  joinToGame,
  removeCurrentGame,
  getPlayerInGame,
  rateGreenCard,
  getGreenCardScore,
  getDiceEndingPosition
};
